import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Wallet, TrendingUp, TrendingDown, Activity, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtAmount } from "@/lib/render";

const COUNT_LABELS = {
  users: "Users", companies: "Companies", customers: "Customers", accounts: "Accounts",
  wallets: "Wallets", services: "Services", currencies: "Currencies", processors: "Processors",
  crypto_wallets: "Crypto Wallets", api_keys: "API Keys",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  const kpis = [
    { key: "total-balance", label: "Total Balance", value: fmtAmount(stats?.total_balance), icon: Wallet, color: "text-indigo-400", ring: "bg-indigo-500/15" },
    { key: "inflow", label: "Total Inflow", value: fmtAmount(stats?.inflow), icon: TrendingUp, color: "text-emerald-400", ring: "bg-emerald-500/15" },
    { key: "outflow", label: "Total Outflow", value: fmtAmount(stats?.outflow), icon: TrendingDown, color: "text-red-400", ring: "bg-red-500/15" },
    { key: "net-flow", label: "Net Flow", value: fmtAmount(stats?.net_flow), icon: Activity, color: "text-cyan-400", ring: "bg-cyan-500/15" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Datacenter Overview</h1>
        <p className="text-sm text-muted-foreground mt-1">Welcome back, {user.name}. Here is the live financial pulse.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <motion.div key={k.key} data-testid={`kpi-${k.key}`}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="rounded-xl border border-border bg-card p-5 hover:border-indigo-500/40 transition-colors">
              <div className="flex items-center justify-between">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{k.label}</p>
                <div className={`h-9 w-9 rounded-lg ${k.ring} flex items-center justify-center`}>
                  <Icon className={`h-4 w-4 ${k.color}`} />
                </div>
              </div>
              <p className="font-mono text-2xl font-semibold mt-3">{stats ? k.value : "…"}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold">Cash Flow</h2>
          <div className="flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1.5 text-emerald-400"><ArrowUpRight className="h-3.5 w-3.5" /> Inflow</span>
            <span className="flex items-center gap-1.5 text-red-400"><ArrowDownRight className="h-3.5 w-3.5" /> Outflow</span>
          </div>
        </div>
        <div className="h-72">
          {stats?.chart?.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.chart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gIn" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EF4444" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="date" stroke="#64748B" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#64748B" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", borderRadius: 8, color: "#F8FAFC" }} />
                <Area type="monotone" dataKey="inflow" stroke="#10B981" fill="url(#gIn)" strokeWidth={2} />
                <Area type="monotone" dataKey="outflow" stroke="#EF4444" fill="url(#gOut)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
              No transaction flow yet — create a transaction to see the chart.
            </div>
          )}
        </div>
      </div>

      {stats?.counts && Object.keys(stats.counts).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {Object.entries(stats.counts).map(([k, v]) => (
            <div key={k} data-testid={`count-${k}`} className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{COUNT_LABELS[k] || k}</p>
              <p className="font-mono text-xl font-semibold mt-1">{v}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
